import { useState } from "react";
import { useLanguage } from "@/LanguageContext";
import { motion } from "framer-motion";
import { Send, Calendar, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_URL = "https://wa.me/41783293318";

export default function ContactSection() {
  const { t, language } = useLanguage();
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, { ...form, language });
      setStatus("success");
      setForm({ name: "", phone: "", message: "" });
      setTimeout(() => setStatus("idle"), 5000);
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 4000);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-32 bg-[#F0EBE3]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm font-body uppercase tracking-[0.2em] text-[#6FA8B6] mb-4">
            {t("contact.label")}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2C3E50]">
            {t("contact.title")}
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="contact-form"
            >
              <div>
                <label className="block font-body text-sm text-[#2C3E50]/70 mb-2">
                  {t("contact.form.name")} *
                </label>
                <Input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder={t("contact.form.name")}
                  required
                  className="rounded-2xl border-[#E5E7EB] bg-[#F0EBE3] h-12 font-body focus:ring-2 focus:ring-[#6FA8B6] focus:border-[#6FA8B6]"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="block font-body text-sm text-[#2C3E50]/70 mb-2">
                  {t("contact.form.phone")} *
                </label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, phone: e.target.value }))
                  }
                  placeholder={t("contact.form.phone")}
                  required
                  className="rounded-2xl border-[#E5E7EB] bg-[#F0EBE3] h-12 font-body focus:ring-2 focus:ring-[#6FA8B6] focus:border-[#6FA8B6]"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <label className="block font-body text-sm text-[#2C3E50]/70 mb-2">
                  {t("contact.form.message")}
                </label>
                <Textarea
                  value={form.message}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, message: e.target.value }))
                  }
                  placeholder={t("contact.form.message")}
                  rows={4}
                  className="rounded-2xl border-[#E5E7EB] bg-[#F0EBE3] font-body resize-none focus:ring-2 focus:ring-[#6FA8B6] focus:border-[#6FA8B6]"
                  data-testid="contact-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full bg-[#6FA8B6] text-white font-body font-medium px-8 py-3.5 rounded-full hover:bg-[#5B8D9A] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="contact-form-submit-button"
              >
                <Send size={16} strokeWidth={1.5} />
                {status === "sending"
                  ? t("contact.form.sending")
                  : t("contact.form.submit")}
              </button>
              {status === "success" && (
                <p
                  className="text-green-600 font-body text-sm text-center"
                  data-testid="contact-success-message"
                >
                  {t("contact.form.success")}
                </p>
              )}
              {status === "error" && (
                <p
                  className="text-red-500 font-body text-sm text-center"
                  data-testid="contact-error-message"
                >
                  {t("contact.form.error")}
                </p>
              )}
            </form>
          </motion.div>

          {/* Right column: Calendly + Quick actions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-6"
          >
            {/* Quick contact actions */}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 bg-[#25D366]/5 rounded-3xl p-6 border border-[#25D366]/20 hover:border-[#25D366]/40 transition-colors group"
              data-testid="contact-whatsapp-link"
            >
              <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center shrink-0">
                <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <div>
                <p className="font-body font-medium text-[#2C3E50] group-hover:text-[#25D366] transition-colors">
                  {t("contact.whatsapp")}
                </p>
                <p className="font-body text-sm text-[#2C3E50]/50">
                  +41 78 329 33 18
                </p>
              </div>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
